import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";

export enum AlternativeDispensingNotDispensableReason {
  CurrentlyUnavailable = "CURRENTLY_UNAVAILABLE",
  DoesNotDispense = "DOES_NOT_DISPENSE",
}

export enum AlternativeDispensingOfferType {
  Cash = "CASH",
  InsuranceEstimate = "INSURANCE_ESTIMATE",
  Prime = "PRIME",
  RxPass = "RX_PASS",
}

export interface AlternativeDispensingPharmacyOffering {
  offerType: AlternativeDispensingOfferType;
  price?: string;
  quantity?: string;
  estimatedDelivery?: string;
}

export interface AlternativeDispensingPharmacyLocation {
  pharmacyId: number;
  pharmacyIdSystem: string;
  pharmacyName: string;
  addressLine_1?: string;
  addressLine_2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  primaryPhone?: string;
  primaryFax?: string;
  pharmacySpecialties: string[];
  serviceLevel?: number;
  latitude?: number;
  longitude?: number;
  ncpdpId: string;
  isAmazonAffiliated: boolean;
  affiliatedNcpdpIds: string[];
  affiliatedPharmacyIds: number[];
}

export interface AlternativeDispensingPharmacy {
  isDrugAvailable: boolean;
  pharmacy: AlternativeDispensingPharmacyLocation;
  notDispensableReason?: AlternativeDispensingNotDispensableReason;
  pharmacyOffers: AlternativeDispensingPharmacyOffering[];
}

export interface AlternativeDispensingPharmaciesResponse {
  alternativeDispensingPharmacies: AlternativeDispensingPharmacy[];
}

export const SAMPLE_DATA: AlternativeDispensingPharmaciesResponse = {
  alternativeDispensingPharmacies: [
    {
      isDrugAvailable: true,
      pharmacy: {
        pharmacyId: 14973,
        pharmacyIdSystem: "https://my.staging.dosespot.com/webapi/v2",
        pharmacyName: "Pillpack.com - Nationwide Home Delivery",
        addressLine_1: "250 Commercial St Ste 2012",
        city: "Manchester",
        state: "NH",
        zipCode: "03101",
        primaryPhone: "8557455725",
        primaryFax: "6039359108",
        pharmacySpecialties: ["24 Hour Pharmacy", "Retail", "EPCS"],
        serviceLevel: 2071,
        ncpdpId: "1655458",
        isAmazonAffiliated: true,
        affiliatedNcpdpIds: [],
        affiliatedPharmacyIds: [],
      },
      pharmacyOffers: [
        {
          offerType: AlternativeDispensingOfferType.Cash,
          price: "22.50",
          quantity: "30 Tablet",
          estimatedDelivery: "Delivery Tomorrow, Mar 13",
        },
        {
          offerType: AlternativeDispensingOfferType.Prime,
          price: "7.53",
          quantity: "30 Tablet",
          estimatedDelivery: "Delivery Tomorrow, Mar 13",
        },
        {
          offerType: AlternativeDispensingOfferType.RxPass,
          price: "0.00",
          quantity: "30 Tablet",
          estimatedDelivery: "Delivery Tomorrow, Mar 13",
        },
        {
          offerType: AlternativeDispensingOfferType.InsuranceEstimate,
          price: "3.00",
          quantity: "30 Tablet",
          estimatedDelivery: "Delivery Tomorrow, Mar 13",
        },
      ],
    },
  ],
};

interface UseAlternativeDispensingPharmaciesParams {
  patientId: string;
  encounterId: string;
  medicationRequestId: string;
  enabled?: boolean;
}

export const useAlternativeDispensingPharmacies = ({
  patientId,
  encounterId,
  medicationRequestId,
  enabled = true,
}: UseAlternativeDispensingPharmaciesParams) => {
  return useApi<AlternativeDispensingPharmaciesResponse>({
    queryOptions: {
      enabled:
        Boolean(patientId && encounterId && medicationRequestId) && enabled,
      queryKey:
        QueryKeys.Patient.Chart.Prescriptions.ALTERNATIVE_DISPENSING_PHARMACIES(
          {
            patientId,
            encounterId,
            medicationRequestId,
          },
        ),
      queryFn: async () => {
        const response =
          await axiosInstance.get<AlternativeDispensingPharmaciesResponse>(
            `/rx/prescriptions/${medicationRequestId}/alternative-dispensing-pharmacies`,
          );
        return response.data;
      },
    },
  });
};
